import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Kpi as KitKpi } from '@nyce/admin-kit/console';
import { StatusBadge, bySeverity, type Tone } from '../../components/status';

// Building blocks shared by the main dashboard and the group dashboards (Operations, Identity, Admin).

export const pct = (v: number | null | undefined, max: number | null | undefined) =>
  v != null && max ? Math.round((v / max) * 100) : null;

export const joinParts = (parts: (string | false | 0 | null | undefined)[]) =>
  parts.filter(Boolean).join(' · ') || undefined;

/** A KPI tile; with `to` the tile links (main dashboard → group dashboard). The kit's `Kpi` over the router's Link. */
export function Kpi(props: { label: string; value: ReactNode; sub?: ReactNode; tone?: Tone; to?: string }) {
  return <KitKpi {...props} Link={Link} />;
}

/** One problem worth a look: `status` is the shared vocabulary (down | error | warn | unknown …). */
export interface Issue {
  key: string;
  status: string;
  name: string;
  /** What kind of thing (namespace · cluster, database, tenant …). */
  what: string;
  detail?: string;
  to?: string | null;
  /** Group label on the main dashboard (Operations, Identity, Admin). */
  group?: string;
}

/** Worst first; an empty list renders as an all-clear line. */
export function IssueList({
  issues,
  title = 'Needs attention',
  max = 12,
  clear = 'Everything is healthy.',
  testId = 'attention',
}: {
  issues: Issue[];
  title?: string;
  max?: number;
  clear?: string;
  testId?: string;
}) {
  const items = [...issues].sort(bySeverity);
  if (!items.length)
    return (
      <div className="nyk-card oph-allclear" data-testid={testId}>
        <CheckCircle2 size={18} aria-hidden="true" />
        <span>{clear}</span>
      </div>
    );
  return (
    <div className="nyk-card" data-testid={testId}>
      <div className="nyk-card-head">
        <h2>{title}</h2>
        <span className="nyk-ui-hint">{items.length}</span>
      </div>
      <ul className="oph-attention">
        {items.slice(0, max).map((i) => (
          <li key={i.key}>
            <StatusBadge status={i.status} />
            <span className="oph-attention-name">
              {i.to ? <Link to={i.to}>{i.name}</Link> : i.name}
              <span className="nyk-ui-hint">
                {' '}
                {i.group ? `${i.group} · ` : ''}
                {i.what}
              </span>
            </span>
            <span className="oph-attention-detail">{i.detail}</span>
          </li>
        ))}
      </ul>
      {items.length > max && <p className="nyk-ui-hint">and {items.length - max} more</p>}
    </div>
  );
}
